import * as stylex from '@stylexjs/stylex'
import { useState } from 'react'

import { WalletMark } from '../../components/SettleUpLogo'
import { SUPPORTED_CURRENCIES } from '../../lib/ledger'
import { IntroPanel } from './LoginPage'


const styles = stylex.create({
  s0: {
    height: "20px",
    width: "20px",
  },
  s1: {
    marginBottom: "8px",
    marginTop: "20px",
    display: "block",
    fontSize: "14px",
    fontWeight: 600,
  },
  s2: {
    display: "flex",
    height: "49px",
    alignItems: "center",
    gap: "12px",
    borderRadius: "7px",
    borderWidth: 1,
    borderColor: "#d9ddda",
    paddingInline: "12px",
    color: "#747b83",
    ":focus-within": {
      borderColor: "#5a8f66",
      boxShadow: "0 0 0 1px color-mix(in srgb, #5a8f66 15%, transparent)",
    },
  },
  s3: {
    minWidth: "0px",
    flex: "1 1 0%",
    borderWidth: 0,
    backgroundColor: "transparent",
    fontSize: "14px",
    color: "#2c302f",
    outline: "none",
    "::placeholder": {
      color: "#8b9198",
    },
  },
  s4: {
    borderRadius: "0.25rem",
    padding: "4px",
    ":hover": {
      backgroundColor: "#f1f4ef",
    },
    ":focus-visible": {
      outlineWidth: 2,
      outlineColor: "#5a8f66",
    },
  },
  s5: {
    minHeight: "100vh",
    width: "100%",
    backgroundColor: "#f4f5f3",
    padding: "4px",
  },
  s6: {
    display: "grid",
    minHeight: "calc(100vh - 8px)",
    width: "100%",
    gridTemplateColumns: "minmax(0,1fr)",
    overflow: "hidden",
    borderRadius: "23px",
    borderWidth: 1,
    borderColor: "#dfe2dd",
    backgroundColor: "#fff",
    boxShadow: "0 1px 8px rgba(44,55,45,.08)",
    "@media (min-width: 1024px)": {
      gridTemplateColumns: "58.5% 41.5%",
    },
  },
  s7: {
    display: "flex",
    minWidth: "0px",
    alignItems: "center",
    justifyContent: "center",
    backgroundImage: "linear-gradient(145deg,#fff 0%,#fcfdfb 100%)",
    paddingInline: "16px",
    paddingBlock: "32px",
    "@media (min-width: 640px)": {
      paddingInline: "24px",
      paddingBlock: "48px",
    },
    "@media (min-width: 1024px)": {},
  },
  s8: {
    width: "100%",
    maxWidth: "426px",
    borderRadius: "19px",
    borderWidth: 1,
    borderColor: "#e0e4df",
    backgroundColor: "#fff",
    paddingInline: "clamp(1.6rem,3.2vw,2.4rem)",
    paddingBlock: "32px",
    boxShadow: "0 6px 18px rgba(50,65,52,.05)",
  },
  s9: {
    marginInline: "auto",
    display: "flex",
    height: "82px",
    width: "82px",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "9999px",
    backgroundColor: "#eff5eb",
  },
  s10: {
    height: "48px",
    width: "56px",
  },
  s11: {
    marginTop: "12px",
    textAlign: "center",
    fontSize: "28px",
    fontWeight: 700,
    letterSpacing: "-.025em",
    color: "#262626",
  },
  s12: {
    marginTop: "4px",
    textAlign: "center",
    fontSize: "15px",
    color: "#697078",
  },
  s13: {
    marginTop: "24px",
  },
  s14: {
    marginBottom: "8px",
    display: "block",
    fontSize: "14px",
    fontWeight: 600,
  },
  s15: {
    height: "49px",
    width: "100%",
    borderRadius: "7px",
    borderWidth: 1,
    borderColor: "#d9ddda",
    backgroundColor: "#fff",
    paddingInline: "12px",
    fontSize: "14px",
    color: "#2c302f",
    outline: "none",
    ":focus": {
      borderColor: "#5a8f66",
      boxShadow: "0 0 0 1px color-mix(in srgb, #5a8f66 15%, transparent)",
    },
  },
  s16: {
    marginTop: "16px",
    borderRadius: "0.5rem",
    backgroundColor: "#fff1eb",
    paddingInline: "12px",
    paddingBlock: "10px",
    fontSize: "12px",
    color: "#a44e20",
  },
  s17: {
    marginTop: "24px",
    display: "flex",
    height: "49px",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    borderRadius: "7px",
    backgroundColor: "#568b62",
    fontSize: "15px",
    fontWeight: 500,
    color: "#fff",
    boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#477a53",
    },
    ":focus-visible": {
      outlineWidth: 2,
      outlineOffset: 2,
      outlineColor: "#477a53",
    },
    ":disabled": {
      cursor: "wait",
      opacity: 0.7,
    },
  },
  s18: {
    marginTop: "24px",
    textAlign: "center",
    fontSize: "13px",
    color: "#687078",
  },
  s19: {
    fontWeight: 600,
    color: "#4f865d",
    ":hover": {
      textDecoration: "underline",
    },
  },
})
type CreateAccountPageProps = {
  onRegister?: (email: string, password: string, displayName: string, currency: string) => Promise<void> | void
  onSignIn?: () => void
}

function FieldIcon({ kind }: { kind: 'email' | 'lock' | 'person' }) {
  if (kind === 'email') {
    return <svg viewBox="0 0 24 24" {...stylex.props(styles.s0)} fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m4 7 8 6 8-6" /></svg>
  }
  if (kind === 'person') {
    return <svg viewBox="0 0 24 24" {...stylex.props(styles.s0)} fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true"><circle cx="12" cy="8" r="3.5" /><path d="M4.5 20v-1.5A5.5 5.5 0 0 1 10 13h4a5.5 5.5 0 0 1 5.5 5.5V20" /></svg>
  }
  return <svg viewBox="0 0 24 24" {...stylex.props(styles.s0)} fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true"><rect x="5" y="10" width="14" height="11" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></svg>
}

function PasswordField({ id, label, value, onChange, autoComplete }: { id: string; label: string; value: string; onChange: (value: string) => void; autoComplete: string }) {
  const [visible, setVisible] = useState(false)
  return (
    <>
      <label htmlFor={id} {...stylex.props(styles.s1)}>{label}</label>
      <div {...stylex.props(styles.s2)}>
        <FieldIcon kind="lock" />
        <input id={id} type={visible ? 'text' : 'password'} autoComplete={autoComplete} placeholder="Create a password" {...stylex.props(styles.s3)} onChange={(event) => onChange(event.target.value)} value={value} />
        <button type="button" aria-label={visible ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`} {...stylex.props(styles.s4)} onClick={() => setVisible((current) => !current)}>
          <svg viewBox="0 0 24 24" {...stylex.props(styles.s0)} fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true"><path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" /><circle cx="12" cy="12" r="2.7" /></svg>
        </button>
      </div>
    </>
  )
}

export default function CreateAccountPage({ onRegister, onSignIn }: CreateAccountPageProps) {
  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [currency, setCurrency] = useState('AWG')
  const [error, setError] = useState('')
  const [creating, setCreating] = useState(false)

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError('')
    if (password !== confirmation) {
      setError('Passwords do not match.')
      return
    }
    setCreating(true)
    try {
      await onRegister?.(email.trim(), password, displayName.trim(), currency)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'We could not create your account. Please try again.')
    } finally {
      setCreating(false)
    }
  }

  return (
    <main {...stylex.props(styles.s5)}>
      <div {...stylex.props(styles.s6)}>
        <IntroPanel />
        <section {...stylex.props(styles.s7)}>
          <div {...stylex.props(styles.s8)}>
            <div {...stylex.props(styles.s9)}><WalletMark {...stylex.props(styles.s10)} /></div>
            <h1 {...stylex.props(styles.s11)}>Create your account</h1>
            <p {...stylex.props(styles.s12)}>Start keeping shared expenses clear.</p>

            <form {...stylex.props(styles.s13)} onSubmit={submit}>
              <label htmlFor="display-name" {...stylex.props(styles.s14)}>Your name</label>
              <div {...stylex.props(styles.s2)}>
                <FieldIcon kind="person" />
                <input id="display-name" required autoComplete="name" placeholder="John Smith" {...stylex.props(styles.s3)} onChange={(event) => setDisplayName(event.target.value)} value={displayName} />
              </div>

              <label htmlFor="create-email" {...stylex.props(styles.s1)}>Email address</label>
              <div {...stylex.props(styles.s2)}>
                <FieldIcon kind="email" />
                <input id="create-email" required type="email" autoComplete="email" placeholder="you@example.com" {...stylex.props(styles.s3)} onChange={(event) => setEmail(event.target.value)} value={email} />
              </div>

              <PasswordField id="create-password" label="Password" value={password} onChange={setPassword} autoComplete="new-password" />
              <PasswordField id="confirm-password" label="Confirm password" value={confirmation} onChange={setConfirmation} autoComplete="new-password" />

              <label htmlFor="default-currency" {...stylex.props(styles.s1)}>Default currency</label>
              <select id="default-currency" value={currency} onChange={(event) => setCurrency(event.target.value)} {...stylex.props(styles.s15)}>
                {SUPPORTED_CURRENCIES.map((option) => <option key={option} value={option}>{option}</option>)}
              </select>

              {error && <p {...stylex.props(styles.s16)} role="alert">{error}</p>}
              <button disabled={creating} type="submit" {...stylex.props(styles.s17)}>
                <FieldIcon kind="person" /> {creating ? 'Creating account…' : 'Create account'}
              </button>
            </form>

            <p {...stylex.props(styles.s18)}>Already have an account? <button type="button" onClick={onSignIn} {...stylex.props(styles.s19)}>Sign in</button></p>
          </div>
        </section>
      </div>
    </main>
  )
}
